import { type DynamicFilterValue, parseDynamicFilters } from '../../filters';

/**
 * Item list / search status values accepted by {@link buildItemSearchFilters}.
 */
export enum ItemStatusFilter {
	AVAILABLE = 'AVAILABLE',
	UNAVAILABLE = 'UNAVAILABLE',
	CHECKED_OUT = 'CHECKED_OUT',
	CHECKING_OUT = 'CHECKING_OUT',
	IN_CUSTODY = 'IN_CUSTODY',
	RETIRED = 'RETIRED',
	IN_WARRANTY = 'IN_WARRANTY',
	OUT_OF_WARRANTY = 'OUT_OF_WARRANTY',
	WITH_FLAG = 'WITH_FLAG',
	WITHOUT_FLAG = 'WITHOUT_FLAG',
}

const statusToListNameMap: Record<string, string | undefined> = {
	[ItemStatusFilter.AVAILABLE]: 'available',
	[ItemStatusFilter.UNAVAILABLE]: 'unavailable',
	[ItemStatusFilter.CHECKED_OUT]: 'checkedout',
	[ItemStatusFilter.CHECKING_OUT]: 'checkingout',
	[ItemStatusFilter.IN_CUSTODY]: 'in_custody',
	[ItemStatusFilter.RETIRED]: 'expired',
	[ItemStatusFilter.IN_WARRANTY]: 'in_warranty_active',
	[ItemStatusFilter.OUT_OF_WARRANTY]: 'out_of_warranty_active',
	[ItemStatusFilter.WITH_FLAG]: undefined,
	[ItemStatusFilter.WITHOUT_FLAG]: undefined,
};

export type ItemSearchFiltersQuery = {
	listNames?: string;
	flag__exists?: boolean;
	category__in?: string[];
	location__in?: string[];
	flag__in?: string[];
	itemTypeId__in?: string[];
	kind?: string;
	kit__exists?: boolean;
	kit__in?: string[];
	custody__in?: string[];
	[key: string]: unknown;
};

export type BuildItemSearchFiltersInput = {
	statuses?: string[];
	categoryIds?: string[];
	locationIds?: string[];
	flagIds?: string[];
	itemTypeIds?: string[];
	kind?: string;
	kitted?: 'kitted' | 'non_kitted';
	kitIds?: string[];
	custodyIds?: string[];
	/** Extra field filters keyed by field id (e.g. `fields.Notes`, `brand`). */
	dynamicFilters?: Record<string, DynamicFilterValue | undefined>;
};

/**
 * Builds Python item search / `customFieldFilters` params from neutral filter ids.
 * UI adapters should unwrap chip shapes before calling this.
 */
export const buildItemSearchFilters = (input: BuildItemSearchFiltersInput = {}): ItemSearchFiltersQuery => {
	const {
		statuses,
		categoryIds,
		locationIds,
		flagIds,
		itemTypeIds,
		kind,
		kitted,
		kitIds,
		custodyIds,
		dynamicFilters = {},
	} = input;

	const query: ItemSearchFiltersQuery = {};

	query.listNames = 'active';
	if (statuses && statuses.length > 0) {
		if (statuses.includes(ItemStatusFilter.WITH_FLAG)) {
			query.flag__exists = true;
		}

		if (statuses.includes(ItemStatusFilter.WITHOUT_FLAG)) {
			query.flag__exists = false;
		}

		query.listNames = statuses
			.map((status) => statusToListNameMap[status])
			.filter((listName): listName is string => Boolean(listName))
			.join(',');
	}

	if (categoryIds && categoryIds.length > 0) {
		query.category__in = categoryIds;
	}

	if (locationIds && locationIds.length > 0) {
		query.location__in = locationIds;
	}

	if (flagIds && flagIds.length > 0) {
		query.flag__in = flagIds;
	}

	if (itemTypeIds && itemTypeIds.length > 0) {
		query.itemTypeId__in = itemTypeIds;
	}

	if (kind) {
		query.kind = kind;
	}

	if (kitted) {
		query.kit__exists = kitted === 'kitted';
	}

	if (kitIds && kitIds.length > 0) {
		query.kit__in = kitIds;
	}

	if (custodyIds && custodyIds.length > 0) {
		query.custody__in = custodyIds;
	}

	return {
		...query,
		...parseDynamicFilters(dynamicFilters),
	};
};
